import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Select, SpinningLoader } from "risa-oasis-ui_v2";
import {
  CsvConfigData,
  NarCsvConfigService,
} from "../../../../../../shared/api/services/narCsvConfigService";
import CsvFieldSection from "./csvFieldSection";

type AuthStatusMapping = {
  auth_contains: string[];
  auth_starts_with: string[];
  auth_indicators: string[];
  no_auth_contains: string[];
  no_auth_starts_with: string[];
  no_auth_indicators: string[];
};

type AuthStatus = "no_auth_required" | "auth_required" | "";

const FIELD_CONFIGS = [
  { key: "auth_contains", title: "Auth Contains" },
  { key: "auth_starts_with", title: "Auth Starts With" },
  { key: "auth_indicators", title: "Auth Indicators" },
  { key: "no_auth_contains", title: "No Auth Contains" },
  { key: "no_auth_starts_with", title: "No Auth Starts With" },
  { key: "no_auth_indicators", title: "No Auth Indicators" },
] as const;

interface CsvConfigDisplayProps {
  csvConfigData: CsvConfigData | null;
  csvLoading: boolean;
  csvError: string | null;
  payerId: string;
  subcollection: string;
  onConfigUpdate?: () => void;
  onRetry?: () => void;
}

const CsvConfigDisplay = ({
  csvConfigData,
  csvLoading,
  csvError,
  payerId,
  subcollection,
  onConfigUpdate,
  onRetry,
}: CsvConfigDisplayProps) => {
  const [authStatusMapping, setAuthStatusMapping] = useState<AuthStatusMapping>(
    {
      auth_contains: [],
      auth_starts_with: [],
      auth_indicators: [],
      no_auth_contains: [],
      no_auth_starts_with: [],
      no_auth_indicators: [],
    },
  );
  const [authStatus, setAuthStatus] = useState<AuthStatus>("");
  const [isSaving, setIsSaving] = useState(false);
  const isInitialLoadRef = useRef(true);

  // Initialize form values when data loads
  useEffect(() => {
    if (csvConfigData) {
      setAuthStatusMapping({
        auth_contains: csvConfigData.auth_status_mapping?.auth_contains ?? [],
        auth_starts_with:
          csvConfigData.auth_status_mapping?.auth_starts_with ?? [],
        auth_indicators:
          csvConfigData.auth_status_mapping?.auth_indicators ?? [],
        no_auth_contains:
          csvConfigData.auth_status_mapping?.no_auth_contains ?? [],
        no_auth_starts_with:
          csvConfigData.auth_status_mapping?.no_auth_starts_with ?? [],
        no_auth_indicators:
          csvConfigData.auth_status_mapping?.no_auth_indicators ?? [],
      });
      setAuthStatus(csvConfigData.column_default_values?.auth_status ?? "");
      isInitialLoadRef.current = true;
      // Set to false after a short delay to allow initial values to settle
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 500);
    }
  }, [csvConfigData]);

  const handleSave = useCallback(async () => {
    if (!payerId || !subcollection || isInitialLoadRef.current) {
      return;
    }

    setIsSaving(true);

    try {
      const updateData: Partial<CsvConfigData> = {
        auth_status_mapping: authStatusMapping,
        ...(authStatus !== "" && {
          column_default_values: {
            auth_status: authStatus as Exclude<AuthStatus, "">,
          },
        }),
      };

      await NarCsvConfigService.updateCsvConfig(
        payerId,
        subcollection,
        updateData,
      );

      toast.success("CSV config saved successfully!");
      if (onConfigUpdate) {
        onConfigUpdate();
      }
    } catch (error) {
      console.error("Error saving CSV config:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save CSV config",
      );
    } finally {
      setIsSaving(false);
    }
  }, [payerId, subcollection, authStatusMapping, authStatus, onConfigUpdate]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    handleSave();
  }, [authStatusMapping, authStatus]);

  if (csvLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <SpinningLoader />
      </div>
    );
  }

  if (csvError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-red-700">{csvError}</p>
          {onRetry && (
            <Button
              buttonType="primary"
              size="small"
              onClick={onRetry}
              disabled={csvLoading}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!csvConfigData) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-gray-600">No data available</p>
          <p className="text-xs text-gray-500">
            CSV configuration data not found for this payer and plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto p-2">
      <div className="flex flex-col gap-6 p-4">
        {/* Auth Status Mapping Section */}
        <div className="flex flex-col gap-6">
          {FIELD_CONFIGS.map((config) => (
            <CsvFieldSection
              key={config.key}
              title={config.title}
              fieldKey={config.key}
              values={authStatusMapping[config.key]}
              onValuesChange={(values) =>
                setAuthStatusMapping((prev) => ({
                  ...prev,
                  [config.key]: values,
                }))
              }
              disabled={isSaving}
            />
          ))}
        </div>

        {/* Column Default Values Section */}
        <div className="rounded-lg border border-primaryGray-14 p-4">
          <div className="mb-3 text-sm font-semibold text-primaryGray-1">
            Auth Status
          </div>
          <Select
            id="auth-status-select"
            label=""
            placeholder="Select auth status"
            options={[
              { label: "No Auth Required", value: "no_auth_required" },
              { label: "Auth Required", value: "auth_required" },
            ]}
            onOptionChange={(data) =>
              setAuthStatus(data.value as Exclude<AuthStatus, "">)
            }
            defaultValue={authStatus || undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default CsvConfigDisplay;
